import {
  CalendarCheck,
  CircleAlert,
  Clock,
  Mail,
  RefreshCw,
} from "lucide-react";
import {
  useGetList,
  useGetMany,
  useGetOne,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";

import type { CrmEvent, Deal, EventCalendarEntry, Partner } from "../types";
import { useCalendarSync } from "./useCalendarSync";

/** An event without an end lasts this long in the calendars. */
const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000;

const isOver = (event: CrmEvent) => {
  const end = event.end_at
    ? new Date(event.end_at)
    : new Date(new Date(event.start_at).getTime() + DEFAULT_DURATION_MS);
  return end.getTime() < Date.now();
};

const KNOWN_ERRORS = [
  "sender_not_connected",
  "auth_failed",
  "unreachable",
  "calendar_not_connected",
];

const StaffCalendarState = ({
  partner,
  entry,
}: {
  partner: Partner;
  entry?: EventCalendarEntry;
}) => {
  const translate = useTranslate();
  if (entry?.status === "synced") {
    return entry.method === "caldav" ? (
      <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
        <CalendarCheck className="size-4 shrink-0" />
        {translate("crm.calendar.state.in_calendar")}
      </span>
    ) : (
      <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
        <Mail className="size-4 shrink-0" />
        {translate("crm.calendar.state.invited", {
          email: entry.attendee_email ?? "",
        })}
      </span>
    );
  }
  if (entry?.status === "error") {
    const code = KNOWN_ERRORS.includes(entry.error ?? "")
      ? entry.error
      : "unknown";
    return (
      <span className="flex items-center gap-1.5 text-destructive">
        <CircleAlert className="size-4 shrink-0" />
        {translate(`crm.calendar.errors.${code}`)}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <Clock className="size-4 shrink-0" />
      {translate(
        partner.email || partner.linked_sales_id
          ? "crm.calendar.state.pending"
          : "crm.calendar.state.unreachable",
      )}
    </span>
  );
};

/**
 * « Agendas du staff » of a saved event: what each staff member received, and
 * a button to try again (after connecting a mailbox, fixing an address...).
 */
export const EventCalendarStatus = ({ event }: { event: CrmEvent }) => {
  const translate = useTranslate();
  const syncCalendar = useCalendarSync();
  const [isSyncing, setSyncing] = useState(false);
  const { data: deal } = useGetOne<Deal>(
    "deals",
    { id: event.deal_id ?? "" },
    { enabled: event.deal_id != null },
  );
  const ids: Identifier[] = [
    ...new Set([...(event.partner_ids ?? []), ...(deal?.partner_ids ?? [])]),
  ];
  const { data: partners = [] } = useGetMany<Partner>(
    "partners",
    { ids },
    { enabled: ids.length > 0 },
  );
  const { data: entries = [] } = useGetList<EventCalendarEntry>(
    "event_calendar_entries",
    {
      filter: { event_id: event.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const staff = partners.filter((partner) => partner.category === "staff");
  if (staff.length === 0 || isOver(event)) return null;

  const sync = async () => {
    setSyncing(true);
    await syncCalendar({ event_id: event.id });
    setSyncing(false);
  };

  return (
    <section className="flex flex-col gap-2 rounded-md border p-3 text-sm">
      <div className="flex items-center gap-2">
        <h3 className="flex-1 font-medium">
          {translate("crm.calendar.title")}
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={sync}
          disabled={isSyncing}
        >
          <RefreshCw className={isSyncing ? "size-4 animate-spin" : "size-4"} />
          {translate("crm.calendar.resync")}
        </Button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {staff.map((partner) => (
          <li
            key={partner.id}
            className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3"
          >
            <span className="sm:w-40 truncate">{partner.name}</span>
            <StaffCalendarState
              partner={partner}
              entry={entries.find((entry) => entry.partner_id === partner.id)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};
